import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Home,
  Heart,
  Package,
  ShoppingBag,
  User,
  Menu,
  Star,
  Users,
} from 'lucide-react';
import { buildPath } from '@/utils/routing';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    path: '/',
    label: 'Dashboard',
    icon: <Home className="w-5 h-5" />,
  },
  {
    path: '/pets',
    label: 'My Pets',
    icon: <Heart className="w-5 h-5" />,
  },
  {
    path: '/subscriptions',
    label: 'Subscriptions',
    icon: <Package className="w-5 h-5" />,
  },
  {
    path: '/orders',
    label: 'Orders',
    icon: <ShoppingBag className="w-5 h-5" />,
  },
  {
    path: '/points',
    label: 'My Points',
    icon: <Star className="w-5 h-5" />,
  },
  {
    path: '/referrals',
    label: 'Referrals',
    icon: <Users className="w-5 h-5" />,
  },
  {
    path: '/profile',
    label: 'Profile',
    icon: <User className="w-5 h-5" />,
  },
];

interface NavigationProps {
  variant?: 'mobile' | 'desktop';
}

export const Navigation: React.FC<NavigationProps> = ({ variant = 'mobile' }) => {
  const location = useLocation();

  if (variant === 'mobile') {
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 safe-area-bottom md:hidden z-50">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const fullPath = buildPath(item.path);
            const isActive = location.pathname === fullPath ||
                           (item.path !== '/' && location.pathname.startsWith(fullPath));

            return (
              <Link
                key={item.path}
                to={fullPath}
                className={clsx(
                  'flex flex-col items-center justify-center gap-1 px-3 py-2 min-w-[60px]',
                  'transition-colors duration-200',
                  {
                    'text-primary-600': isActive,
                    'text-neutral-600': !isActive,
                  }
                )}
              >
                <div
                  className={clsx(
                    'transition-transform duration-200',
                    {
                      'scale-110': isActive,
                    }
                  )}
                >
                  {item.icon}
                </div>
                <span className="text-xs font-medium">{item.label}</span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-gradient-primary rounded-t-full" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    );
  }

  // Desktop sidebar navigation
  return (
    <nav className="hidden md:flex md:flex-col w-64 bg-white border-r border-neutral-200 h-screen sticky top-0">
      <div className="p-6">
        <h2 className="text-2xl font-display font-bold text-gradient-primary">
          AIVA
        </h2>
      </div>

      <div className="flex-1 px-3 space-y-1">
        {navItems.map((item) => {
          const fullPath = buildPath(item.path);
          const isActive = location.pathname === fullPath ||
                         (item.path !== '/' && location.pathname.startsWith(fullPath));

          return (
            <Link
              key={item.path}
              to={fullPath}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg',
                'transition-all duration-200 font-medium',
                {
                  'bg-primary-50 text-primary-600': isActive,
                  'text-neutral-700 hover:bg-neutral-50': !isActive,
                }
              )}
            >
              {item.icon}
              <span>{item.label}</span>
              {isActive && (
                <div className="ml-auto w-1 h-6 bg-gradient-primary rounded-full" />
              )}
            </Link>
          );
        })}
      </div>

      <div className="p-4 border-t border-neutral-200">
        <button className="flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-700 hover:bg-neutral-50 w-full transition-colors">
          <Menu className="w-5 h-5" />
          <span>More Options</span>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
